import { Suspense } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  UserCog,
  BarChart3,
  TrendingUp,
  MousePointerClick,
  FileBarChart,
  LineChart,
  Zap,
  Megaphone,
  Settings as SettingsIcon,
  History,
  BookOpen,
  Mail,
  Database,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { useAdminSupport } from "@/hooks/useAdminSupport";

type Item = { label: string; to: string; icon: React.ElementType; end?: boolean; superOnly?: boolean };
type Group = { title: string; items: Item[] };

const GROUPS: Group[] = [
  {
    title: "Customers",
    items: [
      { label: "Organisations", to: "/admin/organisations", icon: Building2 },
      { label: "Users", to: "/admin/users", icon: Users },
      { label: "Subscriptions", to: "/admin/subscriptions", icon: CreditCard },
      { label: "Impersonate", to: "/admin/impersonate", icon: UserCog, superOnly: true },
    ],
  },
  {
    title: "Analytics",
    items: [
      { label: "Dashboard", to: "/admin", icon: LayoutDashboard, end: true },
      { label: "Usage", to: "/admin/usage", icon: BarChart3 },
      { label: "Churn", to: "/admin/churn", icon: TrendingUp },
      { label: "CTA analytics", to: "/admin/cta-analytics", icon: MousePointerClick },
      { label: "Blog analytics", to: "/admin/blog/analytics", icon: FileBarChart },
      { label: "Product & Revenue", to: "/admin/product-revenue", icon: LineChart },
    ],
  },
  {
    title: "Product",
    items: [
      { label: "Feature flags", to: "/admin/feature-flags", icon: Zap, superOnly: true },
      { label: "Announcements", to: "/admin/announcements", icon: Megaphone },
    ],
  },
  {
    title: "System",
    items: [
      { label: "Team", to: "/admin/team", icon: Users, superOnly: true },
      { label: "Settings", to: "/admin/settings", icon: SettingsIcon, superOnly: true },
      { label: "Audit log", to: "/admin/audit", icon: History },
      { label: "Blog CMS", to: "/admin/blog", icon: BookOpen },
      { label: "Newsletter", to: "/admin/newsletter", icon: Mail },
      { label: "Embed queue", to: "/admin/embed-queue", icon: Database, superOnly: true },
    ],
  },
];

const SAND_BG = "#f4f0e8";
const SIDEBAR_BG = "#faf7f1";
const HAIRLINE = "rgba(58, 53, 48, 0.10)";
const INK = "#3a3530";
const INK_SOFT = "rgba(58, 53, 48, 0.55)";

export function AdminLayout() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAdminSupport();
  const groups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => isSuperAdmin || !i.superOnly),
  })).filter((g) => g.items.length > 0);

  return (
    <div
      className="admin-scope min-h-screen flex w-full"
      style={{
        background: SAND_BG,
        color: INK,
        fontFamily: 'Figtree, "Geist", -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <style>{`
        /* ─── Admin editorial scope ───────────────────────────────────
           Reskin common patterns without touching every tab file. */

        .admin-scope { font-feature-settings: "ss01", "cv11"; }

        /* Tables — hairline rows, uppercase headers, generous padding */
        .admin-scope table { border-collapse: separate; border-spacing: 0; }
        .admin-scope table thead th {
          background: rgba(58, 53, 48, 0.025);
          border-bottom: 1px solid rgba(58, 53, 48, 0.10);
          font-family: Figtree, sans-serif;
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(58, 53, 48, 0.55);
          padding: 14px 24px;
          text-align: left;
        }
        .admin-scope table tbody td {
          padding: 14px 24px;
          border-top: 1px solid rgba(58, 53, 48, 0.06);
          font-size: 13px;
          color: #3a3530;
          vertical-align: middle;
        }
        .admin-scope table tbody tr:first-child td { border-top: none; }
        .admin-scope table tbody tr {
          transition: background-color 120ms ease;
        }
        .admin-scope table tbody tr:hover { background: rgba(194, 84, 26, 0.04); }

        /* Inputs / textareas — ivory paper, hairline, orange focus */
        .admin-scope input[type="text"],
        .admin-scope input[type="email"],
        .admin-scope input[type="search"],
        .admin-scope input[type="password"],
        .admin-scope input[type="number"],
        .admin-scope input:not([type]),
        .admin-scope textarea {
          background: #faf7f1;
          border: 1px solid rgba(58, 53, 48, 0.14);
          border-radius: 3px;
          color: #3a3530;
          transition: border-color 120ms ease, box-shadow 120ms ease;
        }
        .admin-scope input::placeholder,
        .admin-scope textarea::placeholder { color: rgba(58, 53, 48, 0.40); }
        .admin-scope input:focus,
        .admin-scope textarea:focus {
          outline: none;
          border-color: #c2541a;
          box-shadow: 0 0 0 3px rgba(194, 84, 26, 0.10);
        }

        /* Checkboxes accent */
        .admin-scope input[type="checkbox"] { accent-color: #c2541a; }

        /* Cards/panels — soften default shadcn card */
        .admin-scope [class*="bg-card"]:not(.admin-keep) {
          background: #faf7f1;
        }

        /* Avatar fallbacks — warm tone instead of primary tint blob */
        .admin-scope .bg-primary\\/10 {
          background: rgba(194, 84, 26, 0.08) !important;
        }

        /* Buttons — soften default rounded */
        .admin-scope button[class*="rounded-md"] { border-radius: 3px; }

        /* Headings inside body use Outfit light */
        .admin-scope .editorial-display {
          font-family: Outfit, sans-serif;
          font-weight: 300;
          letter-spacing: -0.015em;
        }

        /* Section divider helper */
        .admin-scope .editorial-divider {
          border-top: 1px solid rgba(58, 53, 48, 0.10);
        }
      `}</style>
      {/* Editorial sidebar */}
      <aside
        className="w-64 shrink-0 flex flex-col"
        style={{ background: SIDEBAR_BG, borderRight: `1px solid ${HAIRLINE}` }}
      >
        {/* Masthead */}
        <div className="px-6 pt-7 pb-5" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
          <div
            className="text-[9px] uppercase tracking-[0.32em]"
            style={{ color: INK_SOFT }}
          >
            Sorvaine
          </div>
          <div
            className="mt-1 text-[20px] leading-none"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 400,
              letterSpacing: '-0.01em',
              color: INK,
            }}
          >
            Atelier
          </div>
          <div className="mt-1.5 text-[11px]" style={{ color: INK_SOFT }}>
            {isSuperAdmin ? "Internal control room" : "Support admin · limited"}
          </div>
        </div>

        <button
          onClick={() => navigate("/dashboard")}
          className="mx-6 mt-5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] transition-opacity hover:opacity-100"
          style={{ color: INK_SOFT }}
        >
          <ArrowLeft className="w-3 h-3" /> Back to app
        </button>

        <nav className="flex-1 overflow-y-auto px-3 mt-6 pb-8 space-y-7">
          {groups.map((group) => (
            <div key={group.title}>
              <div
                className="px-3 mb-2 text-[9px] uppercase tracking-[0.28em]"
                style={{ color: INK_SOFT }}
              >
                {group.title}
              </div>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 pl-4 pr-3 h-9 rounded-md text-[13px] transition-colors",
                      )
                    }
                    style={({ isActive }) =>
                      isActive
                        ? {
                            color: INK,
                            background: 'rgba(194, 84, 26, 0.07)',
                            fontWeight: 500,
                          }
                        : { color: INK_SOFT }
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          aria-hidden
                          className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r"
                          style={{
                            background: isActive ? '#c2541a' : 'transparent',
                          }}
                        />
                        <item.icon
                          className="w-3.5 h-3.5"
                          style={{ color: isActive ? '#c2541a' : INK_SOFT }}
                        />
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div
          className="px-6 py-4 text-[10px] uppercase tracking-[0.22em]"
          style={{ color: INK_SOFT, borderTop: `1px solid ${HAIRLINE}` }}
        >
          v · admin · {new Date().getFullYear()}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <ImpersonationBanner />
        <div className="flex-1 overflow-y-auto">
          <div className="px-10 lg:px-14 py-10 max-w-6xl mx-auto w-full">
            <Suspense fallback={<AdminRouteSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </main>
    </div>
  );
}

function AdminRouteSkeleton() {
  const BAR = "rgba(58, 53, 48, 0.08)";
  const SHIMMER = "rgba(58, 53, 48, 0.04)";
  return (
    <div className="animate-pulse space-y-8">
      <div className="space-y-3">
        <div className="h-3 w-32 rounded" style={{ background: BAR }} />
        <div className="h-9 w-2/3 rounded" style={{ background: BAR }} />
        <div className="h-3 w-1/2 rounded" style={{ background: SHIMMER }} />
      </div>
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 rounded-[10px]" style={{ background: SHIMMER, border: "1px solid rgba(58,53,48,0.08)" }} />
        ))}
      </div>
      <div className="space-y-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 rounded" style={{ background: SHIMMER }} />
        ))}
      </div>
    </div>
  );
}
